import { Box, Card, Chip } from "@mui/material";
import ActionButton from "../../components/common/buttons/ActionButton";
import CancelButton from "../../components/common/buttons/CancelButton";
import { Col, Row, message } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { SalonAPI } from "../../api/Salon";
import UploadImageServe from "../../components/common/UploadImageServe";
import UploadMultiImage from "../../components/common/UploadMultiImage";
import { ImageItem } from "../../utils/types";

const FALLBACK_MAX_PHOTOS = 10;
// Gallery slots per row: 10 photos -> two even rows of 5
const GALLERY_COLUMNS = 5;
// Featured photo takes this many of 24 grid columns; the gallery gets the rest
const FEATURED_COL_SPAN = 6;

const Photos = ({
  data,
  onChange,
  onNext,
  onBack
}: {
  data: PhotosSalon;
  onChange?: (data: PhotosSalon) => void;
  onNext?: (tabName: string) => void;
  onBack?: () => void;
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [maxPhotos, setMaxPhotos] = useState<number>(FALLBACK_MAX_PHOTOS);
  const [featured, setFeatured] = useState<string>("");
  const [images, setImages] = useState<ImageItem[]>([]);
  const [pendingChange, setPendingChange] = useState<PendingPhotoChange | null>(null);

  const [loadingGallery, setLoadingGallery] = useState(false);
  const isEdit = !!id;

  useEffect(() => {
    SalonAPI.getPhotoLimit()
      .then((res) => {
        const limit = res.data?.data?.max_photo_count ?? res.data?.max_photo_count;
        if (typeof limit === "number" && limit > 0) setMaxPhotos(limit);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    setFeatured(data?.featured_photo ?? "");
    setImages(data?.photos ?? []);
    setPendingChange(data?.pending_change ?? null);
  }, [data]);

  // Photos the owner uploaded that await approval are shown in place (read-only):
  // a replacement sits in the slot of the photo it replaces, new photos follow the gallery.
  const pendingReplacements = Object.fromEntries(
    (pendingChange?.replacements ?? []).map((item) => [item.from, item.to])
  );
  const allPendingAdded = pendingChange?.added ?? [];
  // A shop without an avatar gets its first new image as the avatar (same rule as the server).
  const pendingAvatarUrl = featured
    ? pendingReplacements[featured]
    : allPendingAdded[0];
  const pendingAdded = featured ? allPendingAdded : allPendingAdded.slice(1);
  const pendingCount = Object.keys(pendingReplacements).length + allPendingAdded.length;

  const handleSaveGallery = async () => {
    const payload = {
      featured_photo: featured,
      photos: images,
    };

    if (isEdit) {
      setLoadingGallery(true);
        SalonAPI.updateSalonPhoto(id, payload).then(() => {
          message.success("Update photos successfully");
          navigate("/salon");
        }).catch(() => {
          message.error("Failed to update photos");
        }).finally(() => {
          setLoadingGallery(false);
        });
    } 
  };

  const handleContinue = () => {
    if (!id && onChange) onChange({
      featured_photo: featured ?? "",
      photos: images ?? [],
    });
    onNext?.("setting");
  };

  return (
    <Box>
      <Card>
        <Row gutter={[24, 24]} className="p-4">
          <Col xs={24} md={FEATURED_COL_SPAN}>
            <div className="mb-3">Shop avatar</div>
            <UploadImageServe
              urlImage={featured}
              onChangeFile={(url) => setFeatured(url || "")}
              loadingProps={false}
              pendingUrl={pendingAvatarUrl}
            />
          </Col>
          <Col xs={24} md={24 - FEATURED_COL_SPAN}>
            <div className="mb-3">Upload more photos (limit {maxPhotos} photos)</div>
            <UploadMultiImage
              listImage={images}
              setListImage={setImages}
              max={maxPhotos}
              columns={GALLERY_COLUMNS}
              pendingReplacements={pendingReplacements}
              pendingAdded={pendingAdded}
            />
            {pendingCount > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <Chip size="small" color="warning" label={`${pendingCount} photo(s) pending review`} />
                <a onClick={() => navigate(`/outlet-change-requests/${pendingChange?.request_id}`)}>
                  View request
                </a>
              </div>
            )}
          </Col>
        </Row>
      </Card>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 2 }}>
        {!id ? (
          <>
            <CancelButton onClick={() => onBack?.()}>Back</CancelButton>
            <ActionButton onClick={handleContinue}>Continue</ActionButton>
          </>
        ) : (
          <>
            <CancelButton onClick={() => navigate("/salon")}>Cancel</CancelButton>
            <ActionButton onClick={handleSaveGallery}>Save</ActionButton>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Photos;
